(function () {
  'use strict';
  if (window.__otfCardColorsLoaded) return;
  window.__otfCardColorsLoaded = true;
  // Existing asset name retained; the shared dialog now owns all size choices.
  // Every product card and product-bound tile is a [data-otf-card] host with the shared body. This script keeps
  // the color band truthful (which exact photos fit, the selected color, the other-color count, the price for a
  // chosen color) and never measures or changes any card's height.
  const HOST = '[data-otf-card]';
  const CUE = '[data-otf-card-color-cue]';
  const SWATCH = '.radio-card-swatch';
  const PREVIEWS = '[data-otf-card-previews]';
  const BAND = '[data-otf-card-colors]';
  const MAX_PREVIEWS = 3;
  const COLLECTION_CARDS = '.template-collection #MainContent .otf-discovery-results #product-grid';
  const isCollectionCard = state => Boolean(state.host.closest(COLLECTION_CARDS));
  const states = new WeakMap();
  const hostResize = typeof ResizeObserver === 'function' ? new ResizeObserver(entries => entries.forEach(entry => {
    const width = entry.contentRect.width;
    if (entry.target._otfLastWidth !== undefined && Math.abs(entry.target._otfLastWidth - width) < 0.01) return;
    entry.target._otfLastWidth = width;
    entry.target.querySelectorAll(CUE).forEach(syncCue);
  })) : null;
  const escapeHtml = value => String(value || '').replace(/[&<>"']/g, c => ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#039;' })[c]);
  const plainText = value => {
    const text = String(value || '');
    if (!text.includes('&')) return text;
    const decoder = document.createElement('textarea');
    decoder.innerHTML = text.replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return decoder.textContent;
  };
  function stateFor(cue) {
    const script = cue.querySelector('[data-otf-card-color-data]');
    if (!script) return null;
    const cached = states.get(cue);
    if (cached?.script === script) return cached;
    try {
      const data = JSON.parse(script.textContent);
      if (!Array.isArray(data.colors)) return null;
      const host = cue.closest(HOST) || cue.parentElement;
      const state = { script, data, host, checked: host.querySelector(`${SWATCH}:checked`) };
      states.set(cue, state);
      if (hostResize && !host._otfResizeObserved) { host._otfResizeObserved = true; hostResize.observe(host); }
      return state;
    } catch (error) { console.error('On Time card color data:', error); return null; }
  }
  function syncPrice(state, selected) {
    const price = state.host.querySelector('[data-otf-card-price]');
    if (!price) return;
    const badge = state.host.querySelector('[data-otf-card-sale-badge]');
    if (!price._otfOriginalPrice) price._otfOriginalPrice = { html: price.innerHTML, className: price.className, badgeHidden: badge?.classList.contains('hidden') || false };
    const summary = selected?.price;
    const starting = state.host.querySelector('[data-otf-card-starting-price]');
    if (starting) {
      const minimum = starting.dataset.otfStartingPriceMin?.trim() ? Number(starting.dataset.otfStartingPriceMin) : NaN;
      const colors = state.data.colors;
      // Shopify sorts by the product-wide minimum. Advertise that amount only when
      // eligible groups confirm it is purchasable; otherwise explain option pricing.
      const complete = colors.length > 0 && colors.every(color => color.eligible === false ||
        (color.eligible === true && Number.isFinite(color.price?.amount) && Boolean(color.price.formatted)));
      const availableMinimum = complete && Number.isFinite(minimum) && minimum >= 0 &&
        colors.some(color => color.eligible && color.price.amount === minimum);
      const amountCue = starting.querySelector('[data-otf-card-starting-amount]');
      const variesCue = starting.querySelector('[data-otf-card-price-varies]');
      const show = complete && Number.isFinite(minimum) && minimum >= 0 && selected?.eligible === true &&
        Number.isFinite(summary?.amount) && !summary.varies && summary.formatted && summary.amount > minimum;
      starting.hidden = !(show && amountCue && variesCue);
      if (amountCue) amountCue.hidden = !show || !availableMinimum;
      if (variesCue) variesCue.hidden = !show || availableMinimum;
    }
    const key = JSON.stringify([selected?.name, summary]);
    if (price._otfColorPriceKey === key) return;
    price._otfColorPriceKey = key;
    // Unknown groups preserve Shopify's original product-price range.
    if (!summary || !Number.isFinite(summary.amount) || !summary.formatted) {
      price.innerHTML = price._otfOriginalPrice.html;
      price.className = price._otfOriginalPrice.className;
      badge?.classList.toggle('hidden', price._otfOriginalPrice.badgeHidden);
      return;
    }
    const strings = state.data.strings || {};
    const sale = Number.isFinite(summary.compare) && summary.compare > summary.amount && Boolean(summary.compareFormatted);
    const moneyMarkup = value => `<span class="otf-card-money">${escapeHtml(plainText(value))}<span class="visually-hidden"> ${escapeHtml(plainText(state.data.currency || ''))}</span></span>`;
    const formatted = summary.varies ? escapeHtml(plainText(strings.fromPrice || '[price]')).replace('[price]', moneyMarkup(summary.formatted)) : moneyMarkup(summary.formatted);
    const unit = summary.unit;
    const unitMarkup = unit ? `<span class="visually-hidden">${escapeHtml(strings.unitPrice)}</span><span class="price-item price-item--last unitprice-item">(<span>${moneyMarkup(unit.formatted)}</span><span aria-hidden="true">/</span><span class="visually-hidden"> ${escapeHtml(strings.unitSeparator)} </span><span>${Number(unit.referenceValue) === 1 ? '' : escapeHtml(unit.referenceValue)}${escapeHtml(unit.referenceUnit)}</span>)</span>` : '';
    price.classList.toggle('price--on-sale', sale);
    price.classList.remove('price--sold-out', 'price--no-compare');
    price.innerHTML = `<div class="price__container${unit ? ' unit__price' : ''}"><div class="price__regular"><span class="visually-hidden visually-hidden--inline">${escapeHtml(strings.regularPrice)}</span><span class="price-item price-item--regular">${formatted}</span></div><div class="price__sale"><span class="visually-hidden visually-hidden--inline">${escapeHtml(strings.salePrice)}</span><span class="price-item price-item--sale price-item--last price--red">${formatted}</span>${sale ? `<span class="visually-hidden visually-hidden--inline">${escapeHtml(strings.regularPrice)}</span><span><s class="price-item price-item--regular">${moneyMarkup(summary.compareFormatted)}</s></span>` : ''}</div><small class="unit-price caption${unit ? '' : ' hidden'}">${unitMarkup}</small></div>`;
    badge?.classList.toggle('hidden', price._otfOriginalPrice.badgeHidden || !sale);
  }
  // How many exact photo previews the color band can hold beside its cue: one centrally defined rule from the
  // band's real width and its tokens (44px targets, 4px gaps, a 52px minimum cue): three from 196px, two from
  // 148px, one from 100px, none (text-only cue) below. Never more than three.
  function bandCapacity(band) {
    const style = getComputedStyle(band);
    const size = parseFloat(style.getPropertyValue('--otf-card-preview')) || 44;
    const gap = parseFloat(style.getPropertyValue('--otf-card-preview-gap')) || 4;
    let cue = parseFloat(style.getPropertyValue('--otf-card-cue-min')) || 52;
    // A translated or larger count keeps its complete text; give up a preview before truncating it.
    const count = band.querySelector('[data-otf-color-count]');
    if (count && !count.hidden) {
      const range = document.createRange();
      range.selectNodeContents(count);
      const textWidth = range.getBoundingClientRect?.().width || count.scrollWidth || 0;
      cue = Math.max(cue, Math.ceil(textWidth));
    }
    const cueFit = cue + 'px';
    if (band.style.getPropertyValue('--otf-card-cue-fit') !== cueFit) band.style.setProperty('--otf-card-cue-fit', cueFit);
    // clientWidth rounds fractional widths and can admit a preview that does not actually fit.
    const width = band.getBoundingClientRect().width - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0) - (parseFloat(style.borderLeftWidth) || 0) - (parseFloat(style.borderRightWidth) || 0);
    if (!(width > 0)) return MAX_PREVIEWS;
    return Math.max(0, Math.min(MAX_PREVIEWS, Math.floor((width - cue) / (size + gap))));
  }
  function layoutColors(state) {
    const row = state.host.querySelector(PREVIEWS);
    if (!row) return;
    const all = [...row.querySelectorAll('.swatch-list')];
    if (!all.length) return;
    const band = row.closest(BAND) || row.parentElement;
    const byName = new Map(state.data.colors.map(color => [color.name, color]));
    // Eligibility: the complete color summary decides when it is exact; otherwise the server-rendered
    // purchasable evidence on the preview itself (data-otf-color-available) decides.
    const eligible = item => {
      const input = item.querySelector(SWATCH);
      const summary = byName.get(input?.value);
      if (summary && typeof summary.eligible === 'boolean') return summary.eligible;
      return input?.dataset.otfColorAvailable !== 'false';
    };
    const collection = isCollectionCard(state);
    const exact = state.data.colors.every(color => typeof color.eligible === 'boolean');
    const capacity = collection && exact ? collectionCapacity(state, band, all, eligible) : bandCapacity(band);
    // The visible set is a function of the band width and the catalog evidence only: the pictured color comes
    // first (server order), then the next eligible photographs. A deliberate color change keeps this set and
    // its geometry; only the checked ring moves.
    const key = capacity + '|' + all.map(item => (eligible(item) ? '1' : '0')).join('');
    if (state.layoutKey === key) return;
    state.layoutKey = key;
    const checked = all.find(item => item.querySelector(`${SWATCH}:checked`));
    const ordered = checked ? [checked, ...all.filter(item => item !== checked)] : all.slice();
    const visible = ordered.filter(eligible).slice(0, capacity);
    if (checked && capacity > 0 && !visible.includes(checked)) { visible.pop(); visible.unshift(checked); }
    const focused = document.activeElement;
    let focusLost = false;
    all.forEach(item => {
      const input = item.querySelector(SWATCH);
      const hidden = !visible.includes(item);
      if (item.hidden !== hidden) item.hidden = hidden;
      if (hidden && focused && item.contains(focused)) focusLost = true;
      item.classList.toggle('otf-color-unavailable', !eligible(item));
      for (let slot = 1; slot <= MAX_PREVIEWS; slot++) item.classList.toggle('otf-card__preview--slot-' + slot, visible[slot - 1] === item);
      if (input) input.tabIndex = hidden ? -1 : 0;
    });
    const hideRow = visible.length === 0;
    if (row.hidden !== hideRow) row.hidden = hideRow;
    if (focusLost) (visible[0]?.querySelector(SWATCH) || state.host.querySelector('[data-otf-open-colors], .otf-card__primary'))?.focus({ preventScroll: true });
    if (collection) band.dataset.otfOverflowReady = '';
  }
  function collectionCapacity(state, band, all, eligible) {
    const style = getComputedStyle(band);
    const size = parseFloat(style.getPropertyValue('--otf-card-preview')) || 44;
    const gap = parseFloat(style.getPropertyValue('--otf-card-preview-gap')) || 4;
    const width = band.getBoundingClientRect().width - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0) - (parseFloat(style.borderLeftWidth) || 0) - (parseFloat(style.borderRightWidth) || 0);
    const names = new Set(state.data.colors.filter(color => color.eligible === true).map(color => color.name));
    const available = [...new Set(all.filter(eligible).map(item => item.querySelector(SWATCH)?.value))].filter(name => names.has(name));
    const count = band.querySelector('[data-otf-color-count]');
    const countStyle = count ? getComputedStyle(count) : style;
    const context = document.createElement('canvas').getContext('2d');
    if (context) context.font = countStyle.font;
    const strings = state.data.strings || {};
    const format = strings.moreCount || strings.other || '+[count]';
    const max = Math.min(MAX_PREVIEWS, available.length);
    let capacity = 0;
    let cueWidth = 52;
    for (let candidate = max; candidate >= 0; candidate--) {
      const hidden = names.size - candidate;
      const label = hidden ? format.replace('[count]', hidden) : '';
      const textWidth = label ? (context?.measureText(label).width || label.length * parseFloat(countStyle.fontSize)) + Math.max(0, label.length - 1) * (parseFloat(countStyle.letterSpacing) || 0) : 0;
      const needed = label ? Math.max(52, Math.ceil(textWidth) + 28) : 52;
      if (!(width > 0) || candidate === 0 || candidate * (size + gap) + needed <= width) {
        capacity = candidate;
        cueWidth = needed;
        break;
      }
    }
    const fit = cueWidth + 'px';
    if (band.style.getPropertyValue('--otf-card-cue-fit') !== fit) band.style.setProperty('--otf-card-cue-fit', fit);
    return capacity;
  }
  // Product-card links carry only "variant" or "otf_color"; every other parameter and #anchor stays as written.
  function cardLinkUrl(url, key, value) {
    const text = String(url || '');
    const hashAt = text.indexOf('#');
    const anchor = hashAt === -1 ? '' : text.slice(hashAt);
    const main = hashAt === -1 ? text : text.slice(0, hashAt);
    const queryAt = main.indexOf('?');
    const path = queryAt === -1 ? main : main.slice(0, queryAt);
    const kept = (queryAt === -1 ? '' : main.slice(queryAt + 1)).split('&').filter(field => {
      const name = field.split('=')[0];
      return field !== '' && name !== 'variant' && name !== 'otf_color';
    });
    if (key) kept.push(key + '=' + encodeURIComponent(value));
    return path + (kept.length ? '?' + kept.join('&') : '') + anchor;
  }
  // Custom tiles (editorial rows, AD ASTRA, Noah, context, cart recommendations) are not .product-grid-item hosts,
  // so global.js does not update them: keep their photo, links and dialog URL in step with the chosen color.
  function syncCustomHost(input) {
    const host = input.closest(HOST);
    if (!host || host.matches('.product-grid-item')) return;
    const productUrl = input.dataset.productUrl;
    const colorId = input.dataset.otfColorId;
    const variantId = input.dataset.variantId;
    const cardUrl = host.dataset.url;
    let selectedUrl = '';
    if (productUrl) selectedUrl = productUrl;
    else if (cardUrl && input.dataset.otfSizeRequired !== 'false') selectedUrl = cardLinkUrl(cardUrl, colorId ? 'otf_color' : '', colorId);
    else if (cardUrl && variantId) selectedUrl = cardLinkUrl(cardUrl, 'variant', variantId);
    if (selectedUrl) {
      host.querySelectorAll('.otf-card__media-link, .otf-card__title-link, a.quick-add__submit, a.otf-card__quick-add--page, a.otf-card-color-more').forEach(link => link.setAttribute('href', selectedUrl));
      host.querySelectorAll('modal-opener button[data-product-url]').forEach(button => button.setAttribute('data-product-url', selectedUrl));
    }
    host.dataset.otfCardColor = input.value;
    host.querySelectorAll('[data-otf-card-color]').forEach(element => { if (element !== host) element.setAttribute('data-otf-card-color', input.value); });
    try {
      const media = JSON.parse(input.dataset.variantMedia || 'null');
      const src = media?.preview_image?.src;
      const photo = host.querySelector('[data-otf-card-media] img');
      if (src && photo) {
        const imageUrl = new URL(src, window.location.origin);
        imageUrl.searchParams.set('width', '600');
        photo.removeAttribute('srcset');
        const key = decodeURIComponent(imageUrl.pathname.split('/').pop()) + (imageUrl.searchParams.has('v') ? '?v=' + imageUrl.searchParams.get('v') : '');
        const prepared = window.otfPreparedImages?.[key]?.url;
        if (prepared) {
          const preparedUrl = new URL(prepared, window.location.origin);
          preparedUrl.searchParams.set('width', '600');
          photo.src = preparedUrl.href;
        } else photo.src = imageUrl.href;
        if (media.alt) photo.alt = media.alt;
      }
    } catch (error) { /* keep the current photograph */ }
    window.otfCustomCardPhotos?.update(host);
  }
  function syncCue(cue) {
    const state = stateFor(cue);
    if (!state) return;
    const name = state.host.querySelector(`${SWATCH}:checked`)?.value || state.host.dataset.otfCardColor || cue.querySelector('[data-otf-color-value]')?.textContent.trim() || state.data.selected || '';
    const selected = state.data.colors.find(color => color.name === name);
    const value = cue.querySelector('[data-otf-color-value]');
    if (value && value.textContent !== name) value.textContent = name;
    const count = cue.querySelector('[data-otf-color-count]');
    const separator = cue.querySelector('[data-otf-color-separator]');
    const collection = isCollectionCard(state);
    layoutColors(state);
    if (count) {
      const exact = state.data.colors.every(color => typeof color.eligible === 'boolean');
      const visibleNames = new Set([...state.host.querySelectorAll(`${PREVIEWS} .swatch-list`)]
        .filter(item => !item.hidden && item.getClientRects().length > 0 && getComputedStyle(item).visibility !== 'hidden')
        .map(item => item.querySelector(SWATCH)?.value).filter(Boolean));
      const availableNames = new Set(state.data.colors.filter(color => color.eligible === true).map(color => color.name));
      const others = [...availableNames].filter(color => !visibleNames.has(color)).length;
      const strings = state.data.strings || {};
      const moreCount = strings.moreCount || strings.other || '+[count]';
      const label = exact ? (others && (availableNames.size > 1 || !availableNames.has(name)) ? (visibleNames.size ? moreCount.replace('[count]', others) : (strings.more || '')) : '') : (strings.more || '');
      if (count.textContent !== label) count.textContent = label;
      count.hidden = !label;
      if (separator) separator.hidden = !label;
      const button = cue.querySelector('[data-otf-open-colors]');
      if (button) {
        button._otfBaseLabel ||= button.getAttribute('aria-label') || '';
        button.setAttribute('aria-label', `${button._otfBaseLabel}: ${name}${label ? `, ${label}` : ''}`);
      }
    }
    syncPrice(state, selected);
  }
  function scan(scope = document) {
    if (scope instanceof Element && scope.matches(CUE)) syncCue(scope);
    scope.querySelectorAll?.(CUE).forEach(syncCue);
  }
  function hasAssignedPhoto(input) {
    try {
      const media = JSON.parse(input.dataset.variantMedia || 'null');
      return Boolean(media?.id && (media.src || media.preview_image?.src) && (!media.media_type || media.media_type === 'image'));
    } catch (error) { return false; }
  }
  // A custom tile whose photograph (and price) live outside the host cannot follow a color change, so a
  // color chosen there would relabel someone else's photograph: such hosts open the chooser instead.
  function fixedContext(host) {
    return Boolean(host && !host.matches('.product-grid-item') && (host.matches('.otf-card--fixed-context') || !host.querySelector('[data-otf-card-media]')));
  }
  function requiresDialog(input) {
    if (!hasAssignedPhoto(input)) return true;
    const host = input.closest(HOST);
    if (fixedContext(host)) return true;
    if (!input.dataset.productUrl || !host?.dataset.url) return false;
    try {
      // A combined-listing sibling has its own complete title, options, price,
      // and photo context; load them together in the native dialog.
      return new URL(input.dataset.productUrl, location.origin).pathname !== new URL(host.dataset.url, location.origin).pathname;
    } catch (error) { return true; }
  }
  function openColorWithoutPhoto(input) {
    const host = input.closest(HOST);
    const opener = host?.querySelector('modal-opener[data-modal]');
    const modal = opener && document.getElementById(opener.dataset.modal.replace(/^#/, ''));
    if (!modal?.show) return false;
    const url = new URL(input.dataset.productUrl || host.dataset.url || host.querySelector('.card__heading a')?.href, location.origin);
    url.searchParams.delete('variant');
    url.searchParams.delete('option_values');
    if (input.dataset.otfColorId) url.searchParams.set('otf_color', input.dataset.otfColorId);
    input.dataset.productUrl = url.toString();
    input.dataset.otfCardColor = input.value;
    input.dataset.otfOpenColors = '';
    modal.show(input);
    return true;
  }
  // If the catalog has no assigned photo for this exact color, open its options
  // instead of pairing a new color label with another color's photograph.
  document.addEventListener('click', event => {
    const label = event.target.closest?.('.swatch-list label');
    const input = label && document.getElementById(label.htmlFor);
    if (!input?.matches(SWATCH) || !requiresDialog(input)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    openColorWithoutPhoto(input);
  }, true);
  document.addEventListener('change', event => {
    const input = event.target;
    if (!input.matches?.(SWATCH)) return;
    const host = input.closest(HOST);
    const cue = host?.querySelector(CUE);
    const state = cue && stateFor(cue);
    if (requiresDialog(input) && state?.checked && state.checked !== input) {
      input.checked = false;
      state.checked.checked = true;
      openColorWithoutPhoto(input);
      event.stopImmediatePropagation();
      return;
    }
    if (state) state.checked = input;
  }, true);
  document.addEventListener('change', event => {
    if (!event.target.matches?.(SWATCH)) return;
    syncCustomHost(event.target);
    event.target.closest(HOST)?.querySelectorAll(CUE).forEach(syncCue);
    // global.js retains the reviewed media swap and selected-color URL logic for .product-grid-item cards.
  });
  function start() {
    scan();
    document.fonts?.ready.then(() => scan());
    new MutationObserver(mutations => {
      for (const mutation of mutations) for (const node of mutation.addedNodes) if (node instanceof Element) scan(node);
    }).observe(document.body, { childList: true, subtree: true });
  }
  let resizeTimer;
  window.addEventListener('resize', () => { clearTimeout(resizeTimer); resizeTimer = setTimeout(() => scan(), 100); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  document.addEventListener('shopify:section:load', event => scan(event.target));
}());


